interface EventDateTimeFields {
  finalDate?: boolean;
  finalTime?: boolean;
  dateTimeBoundaries?: string;
}

interface DateTimeTooltipTranslations {
  dateAndTimeAreSubjectToChange?: string;
  dateIsSubjectToChange?: string;
  timeIsSubjectToChange?: string;
}

interface DateTimeLabelTranslations {
  dateAndTimeNotFinal?: string;
  dateNotFinal?: string;
  timeNotFinal?: string;
}

/**
 * True only when the *date* itself is still tentative. A kick-off time that is
 * not final does not qualify: consumers that key off this — the package date
 * pickers — care exclusively about whether the day can still move.
 */
export const isEventDateNotFinal = (
  event?: EventDateTimeFields | null
): boolean => event?.finalDate === false;

export const getDateTimeBoundariesText = (
  event?: EventDateTimeFields | null
): string | null => {
  const boundaries = event?.dateTimeBoundaries?.trim();
  return boundaries || null;
};

/**
 * Short badge label naming exactly what is tentative. The explanation itself
 * lives in `getDateNotFinalTooltipText`.
 */
export const getDateNotFinalLabel = (
  event: EventDateTimeFields,
  translations: DateTimeLabelTranslations
): string | null => {
  if (event.finalDate === false && event.finalTime === false) {
    return translations.dateAndTimeNotFinal ?? null;
  }
  if (event.finalDate === false) {
    return translations.dateNotFinal ?? null;
  }
  if (event.finalTime === false) {
    return translations.timeNotFinal ?? null;
  }
  return null;
};

export const getDateNotFinalTooltipText = (
  event: EventDateTimeFields,
  translations: DateTimeTooltipTranslations
): string | null => {
  const boundaries = getDateTimeBoundariesText(event);
  const dateAndTimeNotFinal =
    event.finalDate === false && event.finalTime === false;
  const onlyDateNotFinal =
    event.finalDate === false && event.finalTime === true;
  const onlyTimeNotFinal =
    event.finalDate === true && event.finalTime === false;

  let genericText: string | null = null;
  if (dateAndTimeNotFinal) {
    genericText = translations.dateAndTimeAreSubjectToChange ?? null;
  } else if (onlyDateNotFinal) {
    genericText = translations.dateIsSubjectToChange ?? null;
  } else if (onlyTimeNotFinal) {
    genericText = translations.timeIsSubjectToChange ?? null;
  }

  if (genericText && boundaries) {
    return `${genericText}\n\n${boundaries}`;
  }

  if (boundaries) {
    return boundaries;
  }

  return genericText;
};
